import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter } from "react-router-dom";
import { useEffect, useState } from "react";
import { Slide, ToastContainer } from "react-toastify";
import clsx from "clsx";
import { extend } from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { useEffectUnsafe } from "./hooks/useEffectUnsafe";
import BackgroundToastSender from "./components/BackgroundToastSender";
import { useGlobalModalContext } from "./providers/GlobalModalProvider";
import { getGlobalConfig } from "./api/misc";
import { socket } from "./config/socket";
import { IAsset, IChain, IContract, IFarm, IPool } from "./utils/interfaces";
import { getListOfFarms } from "./api/farms";
import { getChainsToConnectTo } from "./utils/chains";
import Landing from "./views/Landing";
import AppRoutes from "Routes";
import { connectWalletWithDispatch, handleAuthentication } from "state/wallet/walletThunks";

import { getListOfAssets, getListOfPools } from "api/pools";
import {
  setLoadingWallet,
  updateAssets,
  updateChains,
  updateContracts,
  updateFarms,
  updatePools,
  updateGlobalConfig,
  updateWsAssetFields,
  updateWsContractFields,
  updateWsFarmFields,
  updateWsPoolFields,
} from "state/wallet/walletSlice";
import { getListOfContracts } from "api/contracts";
import { getChains } from "api/chains";
import { WHITE_LIST_PERSISTED_STATE_KEYS, loadState } from "state/persist";
import { useAppDispatch } from "state";
import { computeRouterHops } from "utils/swapScripts/preComputedRouterHops";

extend(localizedFormat);

function App() {
  const dispatch = useAppDispatch();
  const { showModal } = useGlobalModalContext();
  const [isMaintenaceMode, setIsMaintenaceMode] = useState("1" === process.env.REACT_APP_MAINTENANCE_MODE);

  // requests that are only triggered on page load
  const getInitApiInfo = async () => {
    try {
      const pools: Record<string, IPool> = {};
      const farms: Record<string, IFarm> = {};
      const assets: Record<string, IAsset> = {};
      const contracts: Record<string, IContract> = {};
      const chains: Record<string, IChain> = {};

      const [assetList, poolList, farmList, contractList, chainsInfo, globalConfig] = await Promise.all([
        getListOfAssets(),
        getListOfPools(),
        getListOfFarms(),
        getListOfContracts(),
        getChains(),
        getGlobalConfig(),
      ]);

      poolList.data?.forEach(pool => {
        pools[pool.address] = pool;
      });

      farmList.data?.forEach(farm => {
        farms[farm.address] = farm;
      });

      assetList.data?.forEach(asset => {
        if (asset.isNative) {
          assets[asset.denom] = asset;
        } else {
          assets[asset.address] = asset;
        }
      });

      contractList.data?.forEach(contract => {
        contracts[contract.label + "_" + contract.contextChainId] = contract;
      });

      chainsInfo.data?.forEach(chain => {
        chains[chain.chainId] = chain;
      });

      computeRouterHops(pools);
      dispatch(updateAssets(assets));
      dispatch(updatePools(pools));
      dispatch(updateFarms(farms));
      dispatch(updateContracts(contracts));
      dispatch(updateChains(chains));
      dispatch(updateGlobalConfig(globalConfig.data));

      // start listening for poolUpdate websocket events from API
      socket.on("poolUpdate", data => {
        try {
          dispatch(updateWsPoolFields(JSON.parse(data)));
        } catch (e) {
          console.log(e);
        }
      });

      socket.on("assetUpdate", data => {
        try {
          dispatch(updateWsAssetFields(JSON.parse(data)));
        } catch (e) {
          console.log(e);
        }
      });

      socket.on("contractUpdate", data => {
        try {
          dispatch(updateWsContractFields(JSON.parse(data)));
        } catch (e) {
          console.log(e);
        }
      });

      socket.on("farmUpdate", data => {
        try {
          dispatch(updateWsFarmFields(JSON.parse(data)));
        } catch (e) {
          console.log(e);
        }
      });

      return {
        globalConfig: globalConfig.data,
        chains,
      };
    } catch (err) {
      console.log(err);
    }
  };

  // requests that get repeated
  const getRefreshData = async () => {
    try {
      const pools = {};
      const assets = {};

      const [assetList, poolList, globalConfig] = await Promise.all([
        getListOfAssets(),
        getListOfPools(),
        getGlobalConfig(), // globalConfig because of statusCounters where RPCs cam start to fail
      ]);
      poolList.data?.forEach(pool => {
        pools[pool.address] = pool;
      });

      assetList.data?.forEach(asset => {
        if (asset.isNative) {
          assets[asset.denom] = asset;
        } else {
          assets[asset.address] = asset;
        }
      });

      computeRouterHops(pools);
      dispatch(updateAssets(assets));
      dispatch(updatePools(pools));
      dispatch(updateGlobalConfig(globalConfig.data));
    } catch (e) {
      console.log(e);
    }
  };

  useEffectUnsafe(() => {
    if (isMaintenaceMode) {
      dispatch(setLoadingWallet(false));
      return;
    }

    const onHeaderLoad = async () => {
      const { globalConfig, chains } = await getInitApiInfo();

      if (loadState(WHITE_LIST_PERSISTED_STATE_KEYS.autoConnectWallet)) {
        const walletInfo = await dispatch(
          connectWalletWithDispatch({ chainsToConnect: getChainsToConnectTo(globalConfig, chains), showModal })
        ).unwrap();
        // if user is autoconnect AND has an old permit on their localstorage, we force the user to re-sign the new permit
        const permits = loadState(WHITE_LIST_PERSISTED_STATE_KEYS.permits);
        if (
          permits &&
          walletInfo?.connectedChains["archway-1"] &&
          permits["archway-1_" + walletInfo.connectedChains["archway-1"].address]
        )
          await dispatch(handleAuthentication());
      } else {
        dispatch(setLoadingWallet(false));
      }
    };

    onHeaderLoad();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    //loader on index.html
    const el = document.querySelector(".indexCustomLoader");
    if (el) {
      el.remove();
    }
    if (isMaintenaceMode) return;
    const interval = setInterval(() => {
      getRefreshData();
    }, 90000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  return (
    <HelmetProvider>
      <BrowserRouter>
        {isMaintenaceMode ? <Landing maintenanceMode={true} /> : <AppRoutes />}
        <ToastContainer
          className={clsx("customToastContainer")}
          bodyClassName="customToastBody"
          toastClassName="customToast"
          position="bottom-right"
          hideProgressBar={true}
          draggable={false}
          transition={Slide}
          theme="dark"
        />
        <BackgroundToastSender />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
